import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { PhotoStreamClient } from '../api/PhotoStreamClient';
import { Photo } from '../api/types';

const apiClient = new PhotoStreamClient(
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001'
);

export const PhotoDetailPage: React.FC = () => {
  const { id: galleryId, photoId } = useParams<{ id: string; photoId: string }>();
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [photo, setPhoto] = useState<Photo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadPhoto = async () => {
      if (!galleryId || !photoId) return;
      
      try {
        setLoading(true);
        
        // First get all photos to find the one with matching ID
        const photos = await apiClient.listPhotos(galleryId);
        const foundPhoto = photos.find(p => p.id === photoId);
        
        if (!foundPhoto) {
          setError('Photo not found');
          return;
        }
        
        setPhoto(foundPhoto);
        
        // Then get the photo URL using the s3Key - request medium size for detail view
        const url = await apiClient.getPhotoViewUrl(foundPhoto.s3Key, 'medium');
        setPhotoUrl(url);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load photo');
      } finally {
        setLoading(false);
      }
    };

    loadPhoto();
  }, [galleryId, photoId]);

  const handleDownload = async () => {
    if (!photo) return;
    
    try {
      const originalUrl = await apiClient.getPhotoViewUrl(photo.s3Key, 'original');
      const link = document.createElement('a');
      link.href = originalUrl;
      link.download = `photo-${photo.id}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  if (!galleryId || !photoId) {
    return (
      <div className="container mx-auto py-8 max-w-4xl">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Invalid Photo</h1>
          <Button asChild>
            <Link to="/">Return to Home</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 max-w-4xl">
      <div className="mb-6">
        <Button variant="outline" asChild>
          <Link to={`/gallery/${galleryId}`}>← Back to Gallery</Link>
        </Button>
      </div>

      {loading && (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      )}

      {error && (
        <div className="text-center py-12">
          <p className="text-destructive mb-4">{error}</p>
          <Button asChild>
            <Link to={`/gallery/${galleryId}`}>Back to Gallery</Link>
          </Button>
        </div>
      )}

      {photoUrl && (
        <div className="space-y-4">
          <div className="flex justify-center">
            <Button onClick={handleDownload} variant="outline">
              Download Original
            </Button>
          </div>
          <div className="text-center">
            <img
              src={photoUrl}
              alt="Full size"
              className="max-w-full max-h-[80vh] mx-auto rounded-lg shadow-lg"
              style={{ objectFit: 'contain' }}
            />
          </div>
        </div>
      )}
    </div>
  );
};
